import { NudosButton, NudosFileInput } from 'components/NudosComponents';
import { useRef, useState } from 'react';
import UploadInfo from './components/UploadInfo/UploadInfo';
import UploadWarning from './components/UploadWarning/UploadWarning';
import UploadFileDefaultText from './components/UploadFileDefaultText/UploadFileDefaultText';
import { UploadFormOption } from './UploadForm.types';
import UploadOptions from './components/UploadOptions/UploadOptions';
import { Link } from 'react-router-dom';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { isFileValid } from 'utils/validateFiles';
import NudosConfirmActionModal from 'components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import { UPLOAD_CATALOGUE_MODAL_TEXT } from '../../UploadCatalog/constants';
import { IUploadInfo } from '../../UploadCatalog/content/uploadCatalogInfo';
import UploadErrors from './components/UploadErrors/UploadErrors';
import './UploadForm.scss';

interface UploadFormProps {
  options?: UploadFormOption[];
  fileInputText?: string | JSX.Element;
  fileInputIsDisabled?: boolean;
  onSubmit?: (file: File) => Promise<void>;
  backLink?: string;
  confirmationModal?: boolean;
  hideAlert?: boolean;
  hideFileInput?: boolean;
  errors?: string[];
  customSubmitSection?: JSX.Element;
  uploadInfo?: IUploadInfo;
  showSeparatorInOptions?: boolean;
  customClassName?: string;
  customContentBeforeOptions?: JSX.Element;
}

/**
 * Componente de formulario para carga masiva de archivos Excel (catálogos, pedidos, etc.)
 *
 * Este componente proporciona una interfaz completa para la carga de archivos en formato .xlsx
 * con validación, opciones configurables, manejo de errores, y confirmación opcional mediante modal.
 * Es utilizado principalmente en los flujos de carga masiva del backoffice para catálogos y pedidos.
 *
 * @param options - Array de opciones configurables (checkboxes/dropdowns) que se muestran antes del input de archivo
 * @param fileInputText - Texto personalizado para el botón de selección de archivo. Si no se proporciona, usa texto por defecto con formatos aceptados
 * @param fileInputIsDisabled - Deshabilita el input de archivo (útil cuando falta seleccionar país u otras configuraciones previas)
 * @param onSubmit - Función asíncrona que se ejecuta al confirmar el envío. Recibe el archivo seleccionado como parámetro
 * @param backLink - Ruta de navegación para el enlace "Salir, y continuar luego". Por defecto va a "/"
 * @param confirmationModal - Si es true, muestra modal de confirmación antes de ejecutar onSubmit (usado para reemplazo de catálogo)
 * @param hideAlert - Oculta la alerta de advertencia sobre el proceso de carga
 * @param hideFileInput - Oculta el input de archivo (usado en step de resumen donde solo se muestran errores)
 * @param errors - Array de strings con errores de validación que se muestran debajo del input
 * @param customSubmitSection - JSX Element personalizado que reemplaza la sección de botones por defecto
 * @param uploadInfo - Objeto con información contextual (título, descripción, icono) que se muestra en la parte superior
 * @param showSeparatorInOptions - Controla si se muestra separador visual entre las opciones. Por defecto true
 * @param customClassName - Clase CSS adicional para personalizar estilos del contenedor principal
 * @param customContentBeforeOptions - JSX Element personalizado que se muestra después del UploadInfo y antes de las opciones
 *
 * @returns JSX.Element - Formulario completo de carga con todas las funcionalidades configuradas
 *
 * @example
 * // Uso básico para carga de catálogo
 * <UploadForm
 *   onSubmit={handleFileUpload}
 *   backLink="/bulk-upload/type-of-charge"
 *   confirmationModal={true}
 *   uploadInfo={{
 *     title: "Sube la plantilla actualizada",
 *     description: "Archivo en formato .xlsx",
 *     icon: "document"
 *   }}
 * />
 */
const UploadForm = ({
  options,
  fileInputText,
  onSubmit,
  backLink,
  confirmationModal,
  fileInputIsDisabled = false,
  hideAlert = false,
  hideFileInput = false,
  errors,
  customSubmitSection,
  uploadInfo,
  showSeparatorInOptions = true,
  customClassName = '',
  customContentBeforeOptions
}: UploadFormProps) => {
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalButtonIsLoading, setModalButtonIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef<File | null>(null);

  const handleFileChange = (file: File | undefined) => {
    if (!file) {
      fileRef.current = null;
      setIsFileSelected(false);
      return;
    }
    const isValid = isFileValid(
      file,
      ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      10,
      'El archivo no es un archivo Excel',
      'El archivo no debe pesar más de 10MB'
    );
    if (isValid) {
      fileRef.current = file;
      setIsFileSelected(true);
      return;
    }
    fileRef.current = null;
    setIsFileSelected(false);
  };

  const handleSubmit = () => {
    if (fileRef.current && confirmationModal) {
      setShowModal(true);
    } else if (fileRef.current) {
      handleConfirmSubmit();
    }
  };

  const buttonText = (
    <div className="submitSectionButtonText">
      <span>Subir y finalizar</span>
      <BordOneToneIcon variant="arrowRight" customWidth="10px" standardSize={52} />
    </div>
  );

  const closeModal = () => {
    setShowModal(false);
  };

  const handleConfirmSubmit = async () => {
    setModalButtonIsLoading(true);
    if (fileRef.current && onSubmit) {
      await onSubmit(fileRef.current);
    }
    setModalButtonIsLoading(false);
    setShowModal(false);
  };

  const handleShowDragOverlay = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleHideDragOverlay = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDropFile = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer?.files!;
    if (files && files.length > 0) {
      handleFileChange(files[0]);
    }
    setIsDragging(false);
  };

  return (
    <>
      {showModal && (
        <NudosConfirmActionModal
          firstButtonAction={closeModal}
          secondButtonAction={handleConfirmSubmit}
          firstButtonText={UPLOAD_CATALOGUE_MODAL_TEXT.firstButtonText}
          secondButtonText={UPLOAD_CATALOGUE_MODAL_TEXT.secondButtonText}
          modalText={UPLOAD_CATALOGUE_MODAL_TEXT.description}
          isLoading={modalButtonIsLoading}
          customHeight="fit-content"
        />
      )}
      <div
        className={`uploadForm ${errors && errors.length > 0 ? 'uploadFormWithErrors' : ''} ${customClassName}`}
        onDragOver={handleShowDragOverlay}
      >
        <UploadInfo
          title={uploadInfo?.title || ''}
          description={uploadInfo?.description || ''}
          icon={uploadInfo?.icon || 'document'}
          fontSize={uploadInfo?.fontSize}
        />
        {!hideAlert && <UploadWarning />}
        {customContentBeforeOptions}
        {options && <UploadOptions options={options || []} showSeparator={showSeparatorInOptions} />}
        {isDragging && (
          <div className="dragAndDrop" onDragLeave={handleHideDragOverlay} onDrop={handleDropFile}>
            <BordOneToneIcon className="downloadButton" variant="download" stroke="white" customWidth="32px" />
            <p>Suelta la plantilla aquí para importar</p>
          </div>
        )}
        {!hideFileInput && (
          <NudosFileInput
            uploadedFileHandler={handleFileChange}
            buttonText={fileInputText || <UploadFileDefaultText acceptedFormats={['.xlsx']} maxFileSize={10} />}
            isFilled={isFileSelected}
            customHeight="100px"
            customWidth="611px"
            isDisabled={fileInputIsDisabled}
            resetSelectionWhenClickingWhenFilled
          />
        )}
        {errors && <UploadErrors errors={errors} />}
        {customSubmitSection ? (
          customSubmitSection
        ) : (
          <section className="submitSection">
            <NudosButton
              isButtonDisabled={!isFileSelected || fileInputIsDisabled}
              customWidth="171px"
              customHeight="44px"
              buttonText={buttonText}
              handleClick={handleSubmit}
              isButtonLoading={modalButtonIsLoading}
            />
            <Link to={backLink || '/'} className="submitSectionBackLink">
              Salir, y continuar luego
            </Link>
          </section>
        )}
      </div>
    </>
  );
};

export default UploadForm;
